import {
  resolvePortalPresetDefaultValue
} from './portal-presets.js'
import {
  parseKmlMissionWithProgress as parseKmlMissionAsync
} from './portal-kml-utils.js'
import {
  parseKmzMissionWithProgress as parseKmzMissionAsync
} from './portal-kmz-mission-utils.js'
import {
  xmlToObj,
  updateXmlByPath,
  updatePointXmlByPath
} from './portal-point-map-utils.js'
import {
  setByPath
} from './portal-waypoint-core-utils.js'
import {
  syncKmzMissionHeadingScopesFromXml
} from './portal-kmz-heading-utils.js'
import {
  syncKmzMissionSpeedScopesFromXml
} from './portal-kmz-speed-utils.js'
import {
  syncKmzMissionGimbalScopesFromXml
} from './portal-kmz-gimbal-utils.js'
import {
  syncKmzMissionPayloadScopesFromXml
} from './portal-kmz-payload-utils.js'
import {
  syncKmzMissionTurnScopesFromXml
} from './portal-kmz-turn-utils.js'
import {
  syncKmzMissionStraightLineScopesFromXml
} from './portal-kmz-straight-line-utils.js'
import {
  syncKmzMissionAltitudeScopesFromXml
} from './portal-kmz-altitude-utils.js'
import {
  startPortalLoadingTask
} from './portal-loading-widget.js'

const KMZ_DOCUMENT_DEFAULT_PATHS = {
  template: [
    'wpml:missionConfig.wpml:flyToWaylineMode',
    'wpml:missionConfig.wpml:finishAction',
    'wpml:missionConfig.wpml:exitOnRCLost',
    'wpml:missionConfig.wpml:executeRCLostAction',
    'wpml:missionConfig.wpml:takeOffSecurityHeight',
    'wpml:missionConfig.wpml:globalTransitionalSpeed',
    'wpml:missionConfig.wpml:droneInfo.wpml:droneEnumValue',
    'wpml:missionConfig.wpml:droneInfo.wpml:droneSubEnumValue',
    'wpml:missionConfig.wpml:waylineAvoidLimitAreaMode',
    'wpml:missionConfig.wpml:payloadInfo.wpml:payloadEnumValue',
    'wpml:missionConfig.wpml:payloadInfo.wpml:payloadSubEnumValue',
    'wpml:missionConfig.wpml:payloadInfo.wpml:payloadPositionIndex',
    'Folder.wpml:templateType',
    'Folder.wpml:waylineCoordinateSysParam.wpml:coordinateMode',
    'Folder.wpml:waylineCoordinateSysParam.wpml:heightMode',
    'Folder.wpml:waylineCoordinateSysParam.wpml:positioningType',
    'Folder.wpml:autoFlightSpeed',
    'Folder.wpml:globalHeight',
    'Folder.wpml:gimbalPitchMode',
    'Folder.wpml:globalWaypointHeadingParam.wpml:waypointHeadingMode',
    'Folder.wpml:globalWaypointHeadingParam.wpml:waypointHeadingAngle',
    'Folder.wpml:globalWaypointHeadingParam.wpml:waypointPoiPoint',
    'Folder.wpml:globalWaypointHeadingParam.wpml:waypointHeadingPoiIndex',
    'Folder.wpml:globalWaypointTurnMode',
    'Folder.wpml:globalUseStraightLine',
    'Folder.wpml:caliFlightEnable',
    'Folder.wpml:payloadParam.wpml:payloadPositionIndex',
    'Folder.wpml:payloadParam.wpml:meteringMode',
    'Folder.wpml:payloadParam.wpml:dewarpingEnable',
    'Folder.wpml:payloadParam.wpml:returnMode',
    'Folder.wpml:payloadParam.wpml:samplingRate',
    'Folder.wpml:payloadParam.wpml:scanningMode',
    'Folder.wpml:payloadParam.wpml:modelColoringEnable'
  ],
  waylines: [
    'wpml:missionConfig.wpml:flyToWaylineMode',
    'wpml:missionConfig.wpml:finishAction',
    'wpml:missionConfig.wpml:exitOnRCLost',
    'wpml:missionConfig.wpml:executeRCLostAction',
    'wpml:missionConfig.wpml:takeOffSecurityHeight',
    'wpml:missionConfig.wpml:globalTransitionalSpeed',
    'wpml:missionConfig.wpml:droneInfo.wpml:droneEnumValue',
    'wpml:missionConfig.wpml:droneInfo.wpml:droneSubEnumValue',
    'wpml:missionConfig.wpml:waylineAvoidLimitAreaMode',
    'wpml:missionConfig.wpml:payloadInfo.wpml:payloadEnumValue',
    'wpml:missionConfig.wpml:payloadInfo.wpml:payloadSubEnumValue',
    'wpml:missionConfig.wpml:payloadInfo.wpml:payloadPositionIndex',
    'Folder.wpml:autoFlightSpeed',
    'Folder.wpml:executeHeightMode'
  ]
}

const KMZ_POINT_DEFAULT_PATHS = [
  'wpml:waylineCoordinateSysParam.wpml:coordinateMode',
  'wpml:waylineCoordinateSysParam.wpml:heightMode',
  'wpml:waylineCoordinateSysParam.wpml:positioningType',
  'wpml:autoFlightSpeed',
  'wpml:globalHeight',
  'wpml:gimbalPitchMode',
  'wpml:globalWaypointHeadingParam.wpml:waypointHeadingMode',
  'wpml:globalWaypointHeadingParam.wpml:waypointHeadingAngle',
  'wpml:globalWaypointHeadingParam.wpml:waypointPoiPoint',
  'wpml:globalWaypointHeadingParam.wpml:waypointHeadingPoiIndex',
  'wpml:globalWaypointTurnMode',
  'wpml:globalUseStraightLine',
  'wpml:executeHeightMode',
  'wpml:ellipsoidHeight',
  'wpml:height',
  'wpml:useGlobalHeight',
  'wpml:useGlobalSpeed',
  'wpml:useGlobalHeadingParam',
  'wpml:useGlobalTurnParam',
  'wpml:gimbalPitchAngle',
  'wpml:useStraightLine',
  'wpml:isRisky',
  'wpml:executeHeight',
  'wpml:waypointSpeed',
  'wpml:waypointHeadingParam.wpml:waypointHeadingMode',
  'wpml:waypointHeadingParam.wpml:waypointHeadingAngle',
  'wpml:waypointHeadingParam.wpml:waypointPoiPoint',
  'wpml:waypointHeadingParam.wpml:waypointHeadingAngleEnable',
  'wpml:waypointHeadingParam.wpml:waypointHeadingPathMode',
  'wpml:waypointHeadingParam.wpml:waypointHeadingPoiIndex',
  'wpml:waypointTurnParam.wpml:waypointTurnMode',
  'wpml:waypointTurnParam.wpml:waypointTurnDampingDist',
  'wpml:waypointGimbalHeadingParam.wpml:waypointGimbalPitchAngle',
  'wpml:waypointGimbalHeadingParam.wpml:waypointGimbalYawAngle',
  'wpml:waypointWorkType'
]

const fnNoopKmzUpdateTouch = function () {}

const applyKmzPresetDefaultsToMission = function (parsedMission) {
  if (!parsedMission || typeof parsedMission !== 'object') return
  const documentTargets = [
    { root: parsedMission.documentEl, paths: KMZ_DOCUMENT_DEFAULT_PATHS.template },
    { root: parsedMission.secondaryDocumentEl, paths: KMZ_DOCUMENT_DEFAULT_PATHS.waylines }
  ]
  documentTargets.forEach((target) => {
    const root = target && target.root
    const paths = Array.isArray(target && target.paths) ? target.paths : []
    if (!root) return
    paths.forEach((path) => {
      const nextValue = resolvePortalPresetDefaultValue(path)
      if (nextValue === undefined) return
      updateXmlByPath(root, path, nextValue, fnNoopKmzUpdateTouch)
    })
  })
  const points = Array.isArray(parsedMission.waypoints) ? parsedMission.waypoints : []
  points.forEach((point) => {
    if (!point || typeof point !== 'object') return
    if (!point.props || typeof point.props !== 'object') {
      point.props = {}
    }
    KMZ_POINT_DEFAULT_PATHS.forEach((path) => {
      const nextValue = resolvePortalPresetDefaultValue(path)
      if (nextValue === undefined) return
      setByPath(point.props, path, nextValue)
      updatePointXmlByPath(point, path, nextValue, function (xmlNode, xmlPath, xmlValue) {
        return updateXmlByPath(xmlNode, xmlPath, xmlValue, fnNoopKmzUpdateTouch)
      })
    })
  })
  syncKmzMissionHeadingScopesFromXml(parsedMission, xmlToObj)
  syncKmzMissionSpeedScopesFromXml(parsedMission, xmlToObj)
  syncKmzMissionGimbalScopesFromXml(parsedMission, xmlToObj)
  syncKmzMissionPayloadScopesFromXml(parsedMission, xmlToObj)
  syncKmzMissionTurnScopesFromXml(parsedMission, xmlToObj)
  syncKmzMissionStraightLineScopesFromXml(parsedMission, xmlToObj)
  syncKmzMissionAltitudeScopesFromXml(parsedMission, xmlToObj)
}

const bindMissionToolbarHandlers = function ({
  $ = window.jQuery,
  _ = window._,
  L = window.L,
  $body = $('body'),
  Swal = window.Swal,
  areaSwal = Swal,
  momentRef = window.moment,
  windowRef = window,
  fnT = function (key) { return String(key || '') },
  fnTF = function (_key, vars) { return String((vars && vars.text) || '') },
  fnGetMap = function () { return null },
  fnGetMissionState = function () { return null },
  fnGetImportItems = function () { return null },
  fnGetImportLineItems = function () { return null },
  fnSetImportKmlFilename = function () {},
  fnParseKmlMission = function () { return null },
  fnParseKmzMission = function () { return null },
  fnApplyKmzMissionTimestamps = function () {},
  fnPlaceMissionWaypointsInViewport = function () {},
  fnRenameWaypointsLinear = function () {},
  buildLocationBasedMissionBaseName: fnBuildLocationBasedMissionBaseName = async function () { return 'mission' },
  fnLoadMissionFromParsedMission = function () {},
  fnOpenProjectSettingsEditor = function () {},
  fnExportCurrentMission = function () { return false },
  fnOpenRoute3DViewer = function () { return false },
  fnCloseMissionVariants = function () { return false },
  fnCloseEditedMission = function () {},
  KML_MAX_FILE_BYTES,
  KMZ_MAX_FILE_BYTES,
} = {}) {

  if (!$body || typeof $body.data !== 'function') return
  if ($body.data('missionToolbarHandlersBound')) return
  $body.data('missionToolbarHandlersBound', true)

  const fnResolveExistingShadowVariantKeys = function (missionState) {
    if (!missionState || !missionState.xmlDoc) return []
    const bundle = missionState.__shadowBundle
    if (!bundle || !bundle.variants || typeof bundle.variants !== 'object') return ['K']
    return ['B', 'K', 'J'].filter((key) => !!bundle.variants[key])
  }

  const fnResolveRequestedShadowVariants = function (action) {
    const normalized = String(action || '').trim().toUpperCase()
    if (normalized === 'B') return ['B']
    if (normalized === 'K') return ['K']
    if (normalized === 'J') return ['J']
    if (normalized === 'BJ') return ['B', 'J']
    if (normalized === 'BKJ') return ['B', 'K', 'J']
    return []
  }

  const fnCanRunMiniAction = function (action) {
    const missionState = fnGetMissionState()
    const requested = fnResolveRequestedShadowVariants(action)
    if (!requested.length) return false
    const existing = fnResolveExistingShadowVariantKeys(missionState)
    return requested.every((key) => existing.includes(key))
  }

  const fnResolveSingleExistingVariantAction = function () {
    const existing = fnResolveExistingShadowVariantKeys(fnGetMissionState())
    return existing.length === 1 ? existing[0] : ''
  }

  const fnShowChooseVariantToast = function (actionType) {
    const normalizedType = String(actionType || '').trim().toLowerCase()
    const actionLabel = normalizedType === 'close'
      ? fnT('layout.main.menu.close_edited_route')
      : fnT('export.kmz_label')
    Swal.fire({
      toast: true,
      icon: 'warning',
      position: 'top',
      showConfirmButton: false,
      timer: 4200,
      timerProgressBar: true,
      title: fnTF('project.menu.choose_variant_prompt_with_action', { action: '<i class="fa-solid fa-triangle-exclamation"></i> ' + actionLabel }),
      html: fnT('project.menu.choose_variant_legend')
    })
  }

  const fnHandleTypeRowClick = function (event, actionSelector, $row, actionType) {
    if ($(event.target).closest(actionSelector).length) return
    const $enabledButtons = $row.find(actionSelector + '[data-portal-menu-enabled="1"]')
    if ($enabledButtons.length > 1) {
      event.preventDefault()
      event.stopPropagation()
      fnShowChooseVariantToast(actionType)
      return
    }
    if ($enabledButtons.length !== 1) return
    const $button = $enabledButtons.first()
    if (!$button.length || $button.prop('disabled')) return
    $button.trigger('click')
  }

  const fnCenterCurrentMission = function () {
    const map = fnGetMap()
    const importItems = fnGetImportItems()
    const importLineItems = fnGetImportLineItems()
    if (!map || !importItems || !importLineItems) return false
    const hasMissionLayers = !!(importItems.getLayers().length || importLineItems.getLayers().length)
    if (!hasMissionLayers) {
      Swal.fire(fnT('center.no_open_mission'))
      return false
    }
    const boundsGroup = L.featureGroup([importItems, importLineItems])
    const bounds = boundsGroup.getBounds()
    if (!(bounds && bounds.isValid && bounds.isValid())) {
      Swal.fire(fnT('center.bounds_unavailable'))
      return false
    }
    map.panTo(bounds.getCenter(), { animate: false })
    map.fitBounds(bounds, {
      padding: [20, 20],
      animate: true
    })
    return true
  }

  $body.find('[data-leaf-new-kml]').on('click', async function () {
    const map = fnGetMap()
    if (!map) return
    try {
      const templateXml = String($('[data-tpl-new-kml-pattern]').text() || '').trim()
      if (!templateXml) {
        Swal.fire(fnT('new_kml.template_missing'))
        return
      }
      const parsedMission = fnParseKmlMission(templateXml)
      if (!parsedMission.waypoints || parsedMission.waypoints.length < 2) {
        Swal.fire(fnT('new_kml.need_two_points'))
        return
      }
      fnPlaceMissionWaypointsInViewport(parsedMission)
      fnRenameWaypointsLinear(parsedMission.waypoints, { start: 1, prefix: 'S', syncDescription: true })
      const missionBase = await fnBuildLocationBasedMissionBaseName(parsedMission)
      const stamp = momentRef().format('YYYYMMDD_HHmmss')
      const fileName = missionBase + '_' + stamp + '.kml'
      const docNameEl = parsedMission.documentEl ? parsedMission.documentEl.getElementsByTagName('name')[0] : null
      if (docNameEl) docNameEl.textContent = fileName.replace(/\.kml$/i, '')
      fnLoadMissionFromParsedMission(parsedMission, fileName, {
        fitBounds: false,
        forceDragMode: false,
        missionType: 'kml'
      })
    } catch (error) {
      console.error(fnT('log.new_kml_create_error'), error)
      Swal.fire(fnT('new_kml.failed'))
    }
  })

  $body.find('[data-leaf-new-kmz]').on('click', async function () {
    const map = fnGetMap()
    if (!map) return
    try {
      const templateXml = String($('[data-tpl-new-kmz-template]').text() || '').trim()
      const waylinesXml = String($('[data-tpl-new-kmz-waylines]').text() || '').trim()
      if (!templateXml || !waylinesXml) {
        Swal.fire(fnT('new_kmz.templates_missing'))
        return
      }
      const parsedMission = fnParseKmzMission(templateXml, waylinesXml)
      applyKmzPresetDefaultsToMission(parsedMission)
      if (!parsedMission.waypoints || parsedMission.waypoints.length < 2) {
        Swal.fire(fnT('new_kmz.need_two_points'))
        return
      }
      const createdAtMs = Date.now()
      fnApplyKmzMissionTimestamps(parsedMission, {
        createTimeMs: createdAtMs,
        updateTimeMs: createdAtMs,
        createIfMissing: true
      })
      fnPlaceMissionWaypointsInViewport(parsedMission)
      fnRenameWaypointsLinear(parsedMission.waypoints, { start: 1, prefix: 'S', syncDescription: true })
      const missionBase = await fnBuildLocationBasedMissionBaseName(parsedMission)
      const stamp = momentRef().format('YYYYMMDD_HHmmss')
      const fileName = missionBase + '_' + stamp + '.kmz'
      fnLoadMissionFromParsedMission(parsedMission, fileName, {
        fitBounds: false,
        forceDragMode: false,
        missionType: 'kmz'
      })
    } catch (error) {
      console.error(fnT('log.new_kmz_create_error'), error)
      Swal.fire(fnT('new_kmz.failed'))
    }
  })

  $body.find('[data-leaf-import]').on('click', function () {
    areaSwal.fire({
      width: 500,
      title: fnT('import.title'),
      html: '<input data-file-input type="file" accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/zip" multiple class="form-control"><label data-import-convert-kmz-wrap class="form-check mt-3"><input data-import-convert-kmz class="form-check-input" type="checkbox" checked><span class="form-check-label" data-import-convert-kmz-label>' + fnT('import.convert_to_kmz_project') + '</span></label><span data-import-icon class="d-inline-block mt-5 mb-5 me-5"><i class="text-body-tertiary fa-solid fa-file-import" style="font-size: 200px"></i></span>',
      didOpen: function (modal) {
        const $modal = $(modal)
        const $file = $modal.find('[data-file-input]')
        const $convertToKmz = $modal.find('[data-import-convert-kmz]')
        $modal.on('dragenter', function (event) {
          event.stopPropagation()
          event.preventDefault()
          $modal.addClass('bg-success-subtle')
        })
        $modal.on('dragover', function (event) {
          event.stopPropagation()
          event.preventDefault()
        })
        const fnParseKmzFile = async function (file) {
          if (!windowRef.JSZip) {
            throw new Error(fnT('import.kmz_missing_jszip'))
          }
          const importTask = startPortalLoadingTask({
            id: 'mission-import',
            title: fnT('import.progress_title') || 'Import',
            status: fnT('import.progress_read_kmz') || 'KMZ fájl olvasása',
            progress: 2,
            mode: 'determinate'
          })
          const buffer = await file.arrayBuffer()
          importTask.update({
            status: fnT('import.progress_open_zip') || 'KMZ csomag megnyitása',
            progress: 12
          })
          const zip = await windowRef.JSZip.loadAsync(buffer)
          const templateEntry = zip.file('wpmz/template.kml') || zip.file('template.kml')
          const waylinesEntry = zip.file('wpmz/waylines.wpml') || zip.file('waylines.wpml')
          if (!templateEntry || !waylinesEntry) {
            importTask.fail({
              status: fnT('import.progress_failed') || 'Import sikertelen'
            })
            throw new Error(fnT('import.kmz_templates_missing'))
          }
          importTask.update({
            status: fnT('import.progress_extract_xml') || 'XML fájlok kinyerése',
            progress: 24
          })
          const templateText = await templateEntry.async('string')
          const waylinesText = await waylinesEntry.async('string')
          importTask.update({
            status: fnT('import.progress_parse_xml') || 'KMZ XML feldolgozása',
            progress: 34
          })
          const parsedMission = await parseKmzMissionAsync(templateText, waylinesText, undefined, {
            onProgress: function (payload) {
              const current = Number((payload && payload.current) || 0)
              const total = Number((payload && payload.total) || 0)
              const percent = total > 0 ? Math.max(0, Math.min(100, Math.round((current / total) * 100))) : 100
              importTask.update({
                status: fnTF('import.progress_waypoints', { current: current, total: total, percent: percent }) || 'Waypointok feldolgozása',
                progress: 34 + (percent * 0.46)
              })
            }
          })
          importTask.update({
            status: fnT('import.progress_render') || 'Importált útvonal betöltése',
            progress: 82
          })
          return {
            parsedMission: parsedMission,
            importTask: importTask
          }
        }
        const fnNormalizeKmzFilename = function (rawName) {
          const safeName = String(rawName || '').trim() || 'mission'
          if (/\.kmz$/i.test(safeName)) return safeName
          if (/\.kml$/i.test(safeName)) return safeName.replace(/\.kml$/i, '.kmz')
          if (/\.[A-Za-z0-9]+$/i.test(safeName)) return safeName.replace(/\.[A-Za-z0-9]+$/i, '.kmz')
          return safeName + '.kmz'
        }
        const fnReadCoordinateTriplet = function (rawCoordText) {
          const firstChunk = String(rawCoordText || '').trim().split(/\s+/).map((item) => String(item || '').trim()).filter(Boolean)[0] || ''
          if (!firstChunk) return null
          const parts = firstChunk.split(',').map((item) => String(item || '').trim())
          if (parts.length < 2) return null
          const lng = Number(parts[0])
          const lat = Number(parts[1])
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
          return {
            lat: lat,
            lng: lng,
            altText: parts.length > 2 ? parts[2] : ''
          }
        }
        const fnSetPlacemarkCoordinates = function (placemarkEl, lat, lng) {
          if (!placemarkEl || typeof placemarkEl.getElementsByTagName !== 'function') return false
          const pointEl = placemarkEl.getElementsByTagName('Point')[0]
          if (!pointEl) return false
          const coordEl = pointEl.getElementsByTagName('coordinates')[0]
          if (!coordEl) return false
          const existing = fnReadCoordinateTriplet(coordEl.textContent)
          const altPart = (existing && String(existing.altText || '').trim()) ? (',' + String(existing.altText || '').trim()) : ''
          const coordText = String(lng) + ',' + String(lat) + altPart
          coordEl.textContent = coordText
          return coordText
        }
        const fnCloneKmzTemplatePointForLatLng = function (templatePoint, lat, lng, index) {
          const nextPoint = _.cloneDeep(templatePoint || {})
          const pointRef = (templatePoint && typeof templatePoint === 'object') ? templatePoint : {}
          nextPoint.idx = index
          nextPoint.latlng = [lat, lng]
          if (!nextPoint.props || typeof nextPoint.props !== 'object') {
            nextPoint.props = {}
          }
          const existingCoords = (nextPoint.props.coordinates && typeof nextPoint.props.coordinates === 'object')
            ? nextPoint.props.coordinates
            : {}
          nextPoint.props.coordinates = _.assign({}, existingCoords, { lat: lat, lng: lng })
          nextPoint.xmlEl = (pointRef.xmlEl && typeof pointRef.xmlEl.cloneNode === 'function')
            ? pointRef.xmlEl.cloneNode(true)
            : null
          nextPoint.linkedXmlEls = Array.isArray(pointRef.linkedXmlEls)
            ? pointRef.linkedXmlEls.map((xmlNode) => (xmlNode && typeof xmlNode.cloneNode === 'function') ? xmlNode.cloneNode(true) : null).filter(Boolean)
            : []
          const pointCoordText = fnSetPlacemarkCoordinates(nextPoint.xmlEl, lat, lng)
          nextPoint.linkedXmlEls.forEach((xmlNode) => {
            fnSetPlacemarkCoordinates(xmlNode, lat, lng)
          })
          if (pointCoordText && nextPoint.props.Point && typeof nextPoint.props.Point === 'object' && Object.prototype.hasOwnProperty.call(nextPoint.props.Point, 'coordinates')) {
            nextPoint.props.Point.coordinates = pointCoordText
          }
          return nextPoint
        }
        const fnBuildKmzProjectFromKmlMission = function (kmlMission) {
          const templateXml = String($('[data-tpl-new-kmz-template]').text() || '').trim()
          const waylinesXml = String($('[data-tpl-new-kmz-waylines]').text() || '').trim()
          if (!templateXml || !waylinesXml) {
            throw new Error(fnT('new_kmz.templates_missing'))
          }
          const kmzMission = fnParseKmzMission(templateXml, waylinesXml)
          applyKmzPresetDefaultsToMission(kmzMission)
          const templateWaypoints = Array.isArray(kmzMission && kmzMission.waypoints) ? kmzMission.waypoints.filter(Boolean) : []
          if (!templateWaypoints.length) {
            throw new Error(fnT('new_kmz.need_two_points'))
          }
          const kmlWaypoints = Array.isArray(kmlMission && kmlMission.waypoints)
            ? kmlMission.waypoints.filter((point) => {
                const lat = Number(point && point.latlng && point.latlng[0])
                const lng = Number(point && point.latlng && point.latlng[1])
                return Number.isFinite(lat) && Number.isFinite(lng)
              })
            : []
          if (!kmlWaypoints.length) {
            throw new Error(fnT('import.invalid_kml'))
          }
          const nextWaypoints = kmlWaypoints.map((point, index) => {
            const lat = Number(point.latlng[0])
            const lng = Number(point.latlng[1])
            const templatePoint = templateWaypoints[index % templateWaypoints.length] || templateWaypoints[templateWaypoints.length - 1]
            return fnCloneKmzTemplatePointForLatLng(templatePoint, lat, lng, index)
          })
          kmzMission.waypoints = nextWaypoints
          kmzMission.lineCoords = nextWaypoints.map((point) => [point.latlng[0], point.latlng[1]])
          kmzMission.polygons = []
          return kmzMission
        }
        const fnFiles = function (files) {
          const shouldConvertKmlToKmz = $convertToKmz.length ? $convertToKmz.is(':checked') : true
          $.each(files, function (_index, file) {
            const ext = String((file.name || '')).substring(String(file.name || '').lastIndexOf('.') + 1).toLowerCase()
            const mime = String(file.type || '').toLowerCase()
            const isKmlExt = (ext === 'kml')
            const isKmzExt = (ext === 'kmz')
            const isKmlMime = (!mime || mime === 'application/vnd.google-earth.kml+xml' || mime === 'application/xml' || mime === 'text/xml')
            const isKmzMime = (!mime || mime === 'application/vnd.google-earth.kmz' || mime === 'application/zip' || mime === 'application/octet-stream')
            const isValidKml = isKmlExt && isKmlMime
            const isValidKmz = isKmzExt && isKmzMime
            if (!isValidKml && !isValidKmz) {
              Swal.close()
              Swal.fire(fnT('import.unsupported_type'))
              return false
            }
            const maxBytes = isValidKmz ? KMZ_MAX_FILE_BYTES : KML_MAX_FILE_BYTES
            if (Number(file.size || 0) > maxBytes) {
              Swal.close()
              Swal.fire(fnTF('import.file_too_large', { type: (isValidKmz ? 'KMZ' : 'KML'), maxMb: Math.round(maxBytes / (1024 * 1024)) }))
              return false
            }
            fnSetImportKmlFilename(file.name)
            if (isValidKmz) {
              void (async function () {
                try {
                  const kmzParseResult = await fnParseKmzFile(file)
                  const parsedMission = kmzParseResult && kmzParseResult.parsedMission ? kmzParseResult.parsedMission : null
                  const importTask = kmzParseResult && kmzParseResult.importTask ? kmzParseResult.importTask : null
                  fnLoadMissionFromParsedMission(parsedMission, file.name, {
                    fitBounds: true,
                    forceDragMode: false,
                    missionType: 'kmz'
                  })
                  if (importTask) {
                    importTask.complete({
                      status: fnT('import.progress_done') || 'Import kész',
                      progress: 100
                    })
                  }
                  Swal.close()
                } catch (error) {
                  console.error(fnT('log.kmz_import_parse_error'), error)
                  Swal.fire((error && error.message) ? error.message : fnT('import.kmz_parse_failed'))
                }
              })()
              return true
            }
            const reader = new FileReader()
            reader.addEventListener('load', function (event) {
              void (async function () {
                let importTask = null
                try {
                  const text = String(event.target.result || '')
                  const trimmed = text.trim()
                  if (!trimmed || trimmed.length < 20 || !(trimmed.includes('<kml') || trimmed.includes('<kml:'))) {
                    Swal.fire(fnT('import.invalid_kml'))
                    return
                  }
                  importTask = startPortalLoadingTask({
                    id: 'mission-import',
                    title: fnT('import.progress_title') || 'Import',
                    status: fnT('import.progress_parse_xml') || 'KML XML feldolgozása',
                    progress: 18,
                    mode: 'determinate'
                  })
                  const parsedMission = await parseKmlMissionAsync(text, undefined, {
                    onProgress: function (payload) {
                      const current = Number((payload && payload.current) || 0)
                      const total = Number((payload && payload.total) || 0)
                      const percent = total > 0 ? Math.max(0, Math.min(100, Math.round((current / total) * 100))) : 100
                      importTask.update({
                        status: fnTF('import.progress_waypoints', { current: current, total: total, percent: percent }) || 'Waypointok feldolgozása',
                        progress: 18 + (percent * 0.52)
                      })
                    }
                  })
                  if (shouldConvertKmlToKmz) {
                    importTask.update({
                      status: fnT('import.progress_convert_kmz') || 'KML konvertálása KMZ projektté',
                      progress: 76
                    })
                    const convertedMission = fnBuildKmzProjectFromKmlMission(parsedMission)
                    const createdAtMs = Date.now()
                    fnApplyKmzMissionTimestamps(convertedMission, {
                      createTimeMs: createdAtMs,
                      updateTimeMs: createdAtMs,
                      createIfMissing: true
                    })
                    const convertedFilename = fnNormalizeKmzFilename(file.name)
                    importTask.update({
                      status: fnT('import.progress_render') || 'Importált útvonal betöltése',
                      progress: 88
                    })
                    fnLoadMissionFromParsedMission(convertedMission, convertedFilename, {
                      fitBounds: true,
                      forceDragMode: false,
                      missionType: 'kmz'
                    })
                    importTask.complete({
                      status: fnT('import.progress_done') || 'Import kész',
                      progress: 100
                    })
                    Swal.close()
                    return
                  }
                  importTask.update({
                    status: fnT('import.progress_render') || 'Importált útvonal betöltése',
                    progress: 88
                  })
                  fnLoadMissionFromParsedMission(parsedMission, file.name, {
                    fitBounds: true,
                    forceDragMode: false,
                    missionType: 'kml'
                  })
                  importTask.complete({
                    status: fnT('import.progress_done') || 'Import kész',
                    progress: 100
                  })
                  Swal.close()
                  return
                } catch (error) {
                  if (importTask) {
                    importTask.fail({
                      status: fnT('import.progress_failed') || 'Import sikertelen'
                    })
                  }
                  console.error(fnT('log.import_parse_error'), error)
                  Swal.fire((error && error.message) ? error.message : fnT('import.file_process_failed'))
                }
              })()
            })
            reader.readAsText(file)
          })
        }
        $modal.on('drop', function (event) {
          event.stopPropagation()
          event.preventDefault()
          $modal.removeClass('bg-success-subtle')
          fnFiles(event.originalEvent.dataTransfer.files)
        })
        $modal.find('[data-import-icon]').on('click', function () {
          $file.trigger('click')
        })
        $file.on('input', function () {
          fnFiles($file.get(0).files)
        })
      }
    })
  })

  $body.find('[data-leaf-project-settings]').on('click', function () {
    fnOpenProjectSettingsEditor()
  })

  $body.find('[data-leaf-export-kml]').on('click', function () {
    fnExportCurrentMission('kml')
  })

  $body.find('[data-leaf-export-kmz]').on('click', function () {
    fnExportCurrentMission({
      type: 'kmz',
      variants: ['K']
    })
  })

  $body.find('[data-leaf-export-kmz-action]').on('click', function () {
    const action = String($(this).data('leafExportKmzAction') || '').trim().toUpperCase()
    if (!action) return
    if (!fnCanRunMiniAction(action)) return
    if (action === 'B') {
      fnExportCurrentMission({ type: 'kmz', variants: ['B'] })
      return
    }
    if (action === 'K') {
      fnExportCurrentMission({ type: 'kmz', variants: ['K'] })
      return
    }
    if (action === 'J') {
      fnExportCurrentMission({ type: 'kmz', variants: ['J'] })
      return
    }
    if (action === 'BJ') {
      fnExportCurrentMission({ type: 'kmz', variants: ['B', 'J'] })
      return
    }
    if (action === 'BKJ') {
      fnExportCurrentMission({ type: 'kmz', variants: ['B', 'K', 'J'] })
      return
    }
    fnExportCurrentMission({ type: 'kmz', variants: ['K'] })
  })

  $body.find('[data-leaf-route-3d]').on('click', function () {
    fnOpenRoute3DViewer()
  })

  $body.find('[data-leaf-center]').on('click', function () {
    fnCenterCurrentMission()
  })

  $body.find('[data-leaf-close-project-action]').on('click', function () {
    const missionState = fnGetMissionState()
    if (!missionState || !missionState.xmlDoc) {
      Swal.fire(fnT('close.no_edited_route'))
      return
    }
    const action = String($(this).data('leafCloseProjectAction') || '').trim().toUpperCase()
    if (!action) return
    const requested = fnResolveRequestedShadowVariants(action)
    if (!requested.length) return
    const bundle = (missionState && missionState.__shadowBundle && typeof missionState.__shadowBundle === 'object')
      ? missionState.__shadowBundle
      : null
    const existing = ['B', 'K', 'J'].filter((key) => {
      if (!bundle || !bundle.variants || typeof bundle.variants !== 'object') {
        return key === 'K'
      }
      return !!bundle.variants[key]
    })
    const deletable = requested.filter((key, idx, arr) => arr.indexOf(key) === idx && existing.includes(key))
    if (!deletable.length) return
    const variantsLabel = deletable.map((key) => '[' + key + ']').join(' ')
    Swal.fire({
      title: fnT('close.delete_title'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: fnT('close.delete_confirm'),
      cancelButtonText: fnT('common.cancel'),
      text: fnTF('close.delete_text', { variants: variantsLabel }) + '\n' + fnT('close.delete_irreversible')
    }).then(function (result) {
      if (!result.isConfirmed) return
      const closeResult = fnCloseMissionVariants(deletable)
      if (closeResult && typeof closeResult.then === 'function') {
        closeResult.catch(function () {})
        return
      }
      if (!closeResult && deletable.includes('K')) {
        fnCloseEditedMission()
      }
    })
  })

  $body.find('[data-leaf-export-kmz-row]').on('click', function (event) {
    fnHandleTypeRowClick(event, '[data-leaf-export-kmz-action]', $(this), 'export')
  })

  $body.find('[data-leaf-close-project-row]').on('click', function (event) {
    fnHandleTypeRowClick(event, '[data-leaf-close-project-action]', $(this), 'close')
  })
}

export {
  bindMissionToolbarHandlers
}
